import { ChevronRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { FoutVak, Kiezer, Kop, Laden, Leeg } from '../onderdelen/ui'
import { api, Fout } from '../lib/api'
import { useFactuur, vergeetFactuur } from '../lib/factuur'
import { geld, inclusief, moment } from '../lib/opmaak'
import { laadWerk, meld, useStaat } from '../lib/staat'
import type { Keuzes } from '../lib/types'

/* ===========================================================================
 *  Boekingen: op welke grootboekrekening deze factuur komt
 *
 *  Zoals Blue10: "5050 - Huur", de relatie eronder, rechts netto en bruto.
 *  Is de factuur gesplitst (expense_regel, 0062), dan een regel per stuk;
 *  anders één regel met het grootboek van de factuur zelf.
 *
 *  Sinds 20 okt 2026 tik je op een regel om de rekening te kiezen, en bij
 *  een gesplitste factuur ook de kostenplaats. Bedragen van de verdeling
 *  aanpassen blijft in TW10 Online: daar zie je meteen of het optelt.
 *
 *  Sinds 23 okt 2026 staat er wat de Boekingsdetails in TW10 Online ook
 *  laten zien: de betalingsconditie en de periode bovenaan, per regel het
 *  btw-percentage en de omschrijving (met de kostenplaats), en het totaal
 *  netto en bruto. Casper: "echt hetgene wat desktop ook heeft".
 * =========================================================================== */

type Open = null | { soort: 'rekening' | 'plaats' | 'wat'; regelId: string | null }

export default function Boekingen({ id }: { id: string }) {
  const { antwoord, op, fout, laden, ververs } = useFactuur(id)
  const ik = useStaat((s) => s.ik)
  const bon = antwoord?.bon
  const [open, setOpen] = useState<Open>(null)
  const [keuzes, setKeuzes] = useState<Keuzes | null>(null)
  /* Wat er na de vraag Wat wil je aanpassen? open moet: pas als die kiezer echt dicht is
     (na zijn history.back), anders sluit diezelfde stap de volgende meteen weer. */
  const daarna = useRef<Open>(null)

  const mag = !!bon && !bon.exactId && ik?.mag.beslissen === true
  const gesplitst = (antwoord?.regels.length ?? 0) > 0

  const regels = !antwoord || !bon ? [] : gesplitst
    ? antwoord.regels.map((r) => ({
        id: r.id as string | null,
        code: r.grootboekCode ?? null,
        plaats: r.kostenplaats ?? null,
        /* Een regel zonder eigen rekening boekt op die van de factuur (zoals boeking). */
        rekening: r.grootboekCode ? `${r.grootboekCode}${r.grootboekNaam ? ' - ' + r.grootboekNaam : ''}`
          : bon.grootboekCode ? `Zoals de factuur: ${bon.grootboekCode}${antwoord.grootboekNaam ? ' - ' + antwoord.grootboekNaam : ''}` : 'Geen grootboekrekening',
        sub: [r.omschrijving, r.kostenplaats ? `kostenplaats ${r.kostenplaats}` : null].filter(Boolean).join(' · '),
        pct: r.btwPct ?? 0,
        netto: r.bedragExcl,
        bruto: Math.round(r.bedragExcl * (1 + (r.btwPct ?? 0) / 100) * 100) / 100,
      }))
    : [{
        id: null,
        code: bon.grootboekCode ?? null,
        plaats: null,
        rekening: bon.grootboekCode ? `${bon.grootboekCode}${antwoord.grootboekNaam ? ' - ' + antwoord.grootboekNaam : ''}` : 'Nog geen grootboekrekening',
        /* Zoals Detail.tsx: de omschrijving van de factuur, anders de leverancier. */
        sub: bon.description || '',
        pct: bon.vatPct ?? 0,
        netto: bon.amountExcl,
        bruto: inclusief(bon),
      }]

  async function tik(regelId: string | null) {
    let k = keuzes
    if (!k) {
      try { k = await api.keuzes(id); setKeuzes(k) } catch (e) { meld(e instanceof Fout ? e.message : String(e), 'fout'); return }
    }
    /* Bij een gesplitste factuur eerst kiezen wat; zonder kostenplaatsen in deze onderneming meteen de rekening. */
    setOpen({ soort: regelId && k.kostenplaatsen.length ? 'wat' : 'rekening', regelId })
  }

  async function bewaar(regelId: string | null, deel: { grootboekCode?: string | null; kostenplaats?: string | null }) {
    try {
      if (regelId) await api.regelWijzigen(id, regelId, deel)
      else await api.wijzigen(id, { grootboekCode: deel.grootboekCode ?? null }, { grootboekCode: bon?.grootboekCode ?? null })
      vergeetFactuur(id)
      await ververs()
      void laadWerk()
      meld('Opgeslagen.')
    } catch (e) {
      if (e instanceof Fout && e.status === 409) { vergeetFactuur(id); void ververs() }
      meld(e instanceof Error ? e.message : String(e), 'fout')
    }
  }

  const regel = open ? regels.find((r) => r.id === open.regelId) : null

  /* De kop en het totaal van de Boekingsdetails in TW10 Online (Detail.tsx). */
  const totaalNetto = Math.round(regels.reduce((t, r) => t + (r.netto || 0), 0) * 100) / 100
  const totaalBruto = Math.round(regels.reduce((t, r) => t + (r.bruto || 0), 0) * 100) / 100
  const twee = (n: number) => String(n).padStart(2, '0')
  const periode = !bon ? '' : bon.boekJaar && bon.boekPeriode
    ? `${bon.boekJaar} ${twee(bon.boekPeriode)}`
    : bon.date ? `${new Date(bon.date).getFullYear()} ${twee(new Date(bon.date).getMonth() + 1)}` : ''

  return (
    <div className="scherm">
      <Kop terugNaar={`/factuur/${encodeURIComponent(id)}`} terugLabel="Factuur" titel="Boekingen" />
      {fout && <FoutVak tekst={fout} opnieuw={() => void ververs()} />}
      {!antwoord && laden && <Laden />}
      {antwoord && bon && (
        <>
          <button className="bijgewerkt" onClick={() => void ververs()}>
            <span className="groot">{moment(op)}</span>
            <span className="klein zacht">{laden ? 'Bezig met bijwerken…' : 'Voor het laatst bijgewerkt'}</span>
          </button>
          <dl className="boek-kop">
            <dt>Betalingsconditie</dt><dd>{bon.betaalconditieNaam ?? bon.betaalconditie ?? '-'}</dd>
            <dt>Periode</dt><dd>{periode || '-'}</dd>
          </dl>
          {regels.length === 0 && <Leeg>Geen boekingsregels.</Leeg>}
          <ul className="lijst">
            {regels.map((r) => {
              const binnen = (
                <>
                  <span className="br-links">
                    <span>{r.rekening}</span>
                    <span className="klein zacht">{r.sub || bon.supplier || ''}</span>
                  </span>
                  <span className="br-rechts">
                    <span>Netto: {geld(r.netto, bon.valuta)}</span>
                    <span className="klein zacht">Bruto: {geld(r.bruto, bon.valuta)}</span>
                    <span className="klein zacht">Btw {r.pct}%</span>
                  </span>
                  {mag && <ChevronRight size={18} className="gegeven-pijl" aria-hidden="true" />}
                </>
              )
              return (
                <li key={r.id ?? 'een'}>
                  {mag
                    ? <button type="button" className="lijst-rij boeking-rij" onClick={() => void tik(r.id)}>{binnen}</button>
                    : <div className="lijst-rij boeking-rij">{binnen}</div>}
                </li>
              )
            })}
          </ul>
          {regels.length > 0 && (
            <div className="boek-totaal">
              <span>{regels.length} {regels.length === 1 ? 'regel' : 'regels'}</span>
              <span>Netto {geld(totaalNetto, bon.valuta)} · Bruto {geld(totaalBruto, bon.valuta)}</span>
            </div>
          )}
          {mag && <p className="klein zacht midden">Tik op een regel om de rekening{gesplitst ? ' of kostenplaats' : ''} te kiezen.</p>}
          {bon.exactId && <p className="klein zacht midden">Deze factuur staat in Exact. Aanpassen doe je daar.</p>}
        </>
      )}

      {open?.soort === 'wat' && (
        <Kiezer
          titel="Wat wil je aanpassen?" terugLabel="Boekingen"
          opties={[
            { waarde: 'rekening', label: 'Grootboekrekening', uitleg: regel?.rekening },
            { waarde: 'plaats', label: 'Kostenplaats', uitleg: regel?.plaats ?? 'Geen' },
          ]}
          gekozen={null}
          kies={(w) => { daarna.current = { soort: w as 'rekening' | 'plaats', regelId: open.regelId } }}
          sluit={() => { setOpen(daarna.current); daarna.current = null }}
        />
      )}
      {open?.soort === 'rekening' && keuzes && (
        <Kiezer
          titel="Grootboekrekening" terugLabel="Boekingen" zoekbaar
          opties={[
            ...(open.regelId ? [{ waarde: '', label: '(Zoals de factuur)' }] : []),
            ...keuzes.rekeningen.map((r) => ({ waarde: r.code, label: `${r.code} - ${r.naam}` })),
          ]}
          gekozen={regel?.code ?? (open.regelId ? '' : null)}
          kies={(w) => void bewaar(open.regelId, { grootboekCode: w || null })}
          sluit={() => setOpen(null)}
          leegTekst="Geen rekeningen gevonden voor deze onderneming."
        />
      )}
      {open?.soort === 'plaats' && keuzes && open.regelId && (
        <Kiezer
          titel="Kostenplaats" terugLabel="Boekingen" zoekbaar
          opties={[{ waarde: '', label: '(Geen)' }, ...keuzes.kostenplaatsen.map((k) => ({ waarde: k.code, label: `${k.code} - ${k.naam}` }))]}
          gekozen={regel?.plaats ?? ''}
          kies={(w) => void bewaar(open.regelId, { kostenplaats: w || null })}
          sluit={() => setOpen(null)}
        />
      )}
    </div>
  )
}
